/**
 * Bóc thông báo lỗi thật từ một lỗi axios.
 *
 * Cần thiết vì hiện có 67 chỗ trong `api/` viết
 * `catch { throw new Error("Có lỗi xảy ra trong quá trình kết nối") }` — nuốt sạch
 * câu lỗi Laravel đã trả về, nên người dùng luôn thấy một câu vô nghĩa cho mọi
 * nguyên nhân khác nhau (sai dữ liệu, trùng số điện thoại, hết quyền...).
 *
 * Khi chuyển một module sang `useAsync`, tầng api không nuốt lỗi nữa mà để lỗi
 * axios nguyên vẹn bay lên; nơi hiển thị gọi `errorMessage(e)`.
 */

/** Người dùng chủ động hủy (đổi bộ lọc, rời trang) — không phải lỗi để hiện ra. */
export function isAborted(error) {
  if (!error) return false;
  if (error.code === "ERR_CANCELED" || error.name === "CanceledError") return true;
  return error instanceof DOMException && error.name === "AbortError";
}

/**
 * Ưu tiên theo thứ tự: câu lỗi field đầu tiên (`errors`) → `message` của BE →
 * câu mặc định theo HTTP status. Không bao giờ trả về "Network Error" trần của
 * axios vì với người dùng nó vô nghĩa.
 */
export function errorMessage(error, fallback = "Đã xảy ra lỗi. Vui lòng thử lại.") {
  if (!error) return fallback;

  const data = error.response?.data;

  if (data?.errors && typeof data.errors === "object") {
    const first = Object.values(data.errors)[0];
    const text = Array.isArray(first) ? first[0] : first;
    if (text) return String(text);
  }

  if (typeof data?.message === "string" && data.message) return data.message;
  if (typeof data === "string" && data) return data;

  const status = error.response?.status;
  if (status) return statusMessage(status, fallback);

  // Không có response = request không tới được server.
  if (error.code === "ECONNABORTED") return "Máy chủ phản hồi quá lâu. Vui lòng thử lại.";
  if (error.request) return "Không kết nối được tới máy chủ.";

  return error.message || fallback;
}

function statusMessage(status, fallback) {
  if (status === 400 || status === 422) return "Dữ liệu không hợp lệ.";
  if (status === 401) return "Phiên đăng nhập đã hết hạn.";
  if (status === 403) return "Bạn không có quyền thực hiện hành động này.";
  if (status === 404) return "Không tìm thấy dữ liệu.";
  if (status === 409) return "Dữ liệu đã tồn tại hoặc đang xung đột.";
  if (status === 429) return "Bạn thao tác quá nhanh. Vui lòng chờ một chút.";
  if (status >= 500) return "Máy chủ đang gặp sự cố. Vui lòng thử lại sau.";
  return fallback;
}

/**
 * Lỗi theo từng field, để tô đỏ đúng ô input thay vì chỉ hiện một toast chung.
 * Trả về `{ [field]: "câu lỗi" }` — khớp dạng `setFields` của antd Form.
 *
 * @example
 * catch (e) {
 *   const fields = fieldErrors(e);
 *   if (fields) form.setFields(Object.entries(fields).map(([name, errs]) => ({ name, errors: [errs] })));
 *   else message.error(errorMessage(e));
 * }
 */
export function fieldErrors(error) {
  const errors = error?.response?.data?.errors;
  if (!errors || typeof errors !== "object") return null;
  const out = {};
  for (const [field, value] of Object.entries(errors)) {
    out[field] = Array.isArray(value) ? value[0] : String(value);
  }
  return Object.keys(out).length ? out : null;
}
